/**
 * To use this component:
 * - add the class .slider--item class on each slider item
 * - put each .slider--item under the same container. e.g. div
 * - put the class .slider---previous-button on the previous element button
 * - put the class .slider---next-button on the next element button
 * - test and adjust the container padding using the container-padding-fix attribute
 * - (optional) put the class .slider--visual-slider on each visual slider element. The number of visual sliders should be equal to the number of slider items and should be in the same index order of the slider items.
 */

class CarouselNavigation extends HTMLElement {
  constructor() {
    super();
    this.containerPaddingFix = 0;
  }

  connectedCallback() {
    this.scrollItems = this.querySelectorAll('.slider--item');
    if (!this.scrollItems.length) {
      return;
    }

    this.scrollContainer = this.scrollItems[0].parentElement;
    this.scrollContainer.addEventListener(
      'scroll',
      debounce(this.debounceScroll.bind(this), ON_CHANGE_DEBOUNCE_TIMER),
    );

    this.previousButtons = this.querySelectorAll('.slider--previous-button');
    this.previousButtons.forEach((el) =>
      el.addEventListener('click', this.scrollToPreviousItem.bind(this)),
    );

    this.nextButtons = this.querySelectorAll('.slider--next-button');
    this.nextButtons.forEach((el) =>
      el.addEventListener('click', this.scrollToNextItem.bind(this)),
    );

    this.visualSliders = this.querySelectorAll('.slider--visual-slider');
  }

  disconnectedCallback() {
    this.scrollContainer.removeEventListener(
      'scroll',
      debounce(this.debounceScroll.bind(this), ON_CHANGE_DEBOUNCE_TIMER),
    );
    this.previousButtons.forEach((el) =>
      el.addEventListener('click', this.scrollToPreviousItem.bind(this)),
    );
    this.nextButtons.forEach((el) =>
      el.addEventListener('click', this.scrollToNextItem.bind(this)),
    );
  }

  static get observedAttributes() {
    return ['container-padding-fix'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'container-padding-fix') {
      try {
        this.containerPaddingFix = parseInt(newValue);
      } catch (err) {
        this.containerPaddingFix = 0;
      }
    }
  }

  scrollToPreviousItem() {
    let index = 0;
    for (let i = 0; i < this.scrollItems.length; i++) {
      if (this.scrollItems[i].classList.contains('active')) {
        if (i - 1 < 0) return;
        index = i - 1;
        break;
      }
    }
    this.scrollToItem(index);
  }

  scrollToNextItem() {
    let index = this.scrollItems.length - 1;
    for (let i = 0; i < this.scrollItems.length; i++) {
      if (this.scrollItems[i].classList.contains('active')) {
        if (i + 1 >= this.scrollItems.length) return;
        index = i + 1;
        break;
      }
    }
    this.scrollToItem(index);
  }

  scrollToItem(scrollIndex) {
    if (scrollIndex < 0 || scrollIndex >= this.scrollItems.length) {
      return;
    }

    this.scrollItems.forEach((it, i) => {
      if (i === scrollIndex) {
        it.classList.add('active');
      } else {
        it.classList.remove('active');
      }
    });

    this.visualSliders.forEach((slider, i) => {
      if (i === scrollIndex) {
        slider.classList.add('active');
      } else {
        slider.classList.remove('active');
      }
    });

    if (scrollIndex === 0) {
      this.previousButtons.forEach((el) =>
        el.setAttribute('disabled', 'disabled'),
      );
    } else {
      this.previousButtons.forEach((el) => el.removeAttribute('disabled'));
    }
    if (scrollIndex === this.scrollItems.length - 1) {
      this.nextButtons.forEach((el) => el.setAttribute('disabled', 'disabled'));
    } else {
      this.nextButtons.forEach((el) => el.removeAttribute('disabled'));
    }

    const rect = this.scrollItems[scrollIndex].getBoundingClientRect();
    this.scrollContainer.scrollLeft += rect.left - this.containerPaddingFix;
  }

  debounceScroll() {
    if (
      this.scrollContainer.scrollLeft >=
      this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth
    ) {
      this.scrollToItem(this.scrollItems.length - 1);
      return;
    }
    for (let i = 0; i < this.scrollItems.length; i++) {
      if (
        this.scrollItems[i].getBoundingClientRect().left -
          this.containerPaddingFix >=
        0
      ) {
        this.scrollToItem(i);
        break;
      }
    }
  }
}

customElements.define('carousel-navigation', CarouselNavigation);
